import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useModals } from '../../hooks/useModals';
import { useNavigate } from 'react-router-dom';
import useDashboardData from '../../contexts/userDashboardData';
import { showToast } from '../../utils/helpers';
import GenerateAccountCard from "../../components/dashboard/GenerateAccountCard"
import GetNewAccountCard from "../../components/dashboard/GetNewAccountCard"
import AccountUnderReviewCard from '../../components/dashboard/AccountUnderReviewCard';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const CompleteSignupPage = () => {
  const { currentUser, currentAccountDetails, existingAccount, refreshUserData, logout } = useAuth();
  const { data, loading, error, refresh: refreshDashboardData } = useDashboardData();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { modals, openModal, closeModal } = useModals();
  const navigate = useNavigate();
  const [hasBankAccount, setHasBankAccount] = useState(false);
  const [checkingBankAccount, setCheckingBankAccount] = useState(true);

  // Detect an existing connected bank account so the complete-profile card is
  // replaced by real content once the user has one.
  useEffect(() => {
    let mounted = true;
    api.get(API_CONFIG.USER.EXISTING_ACCOUNTS)
      .then((res) => {
        if (mounted && res.data?.success) {
          setHasBankAccount((res.data.data || []).length > 0);
        }
      })
      .catch(() => {})
      .finally(() => { if (mounted) setCheckingBankAccount(false); });
    return () => { mounted = false; };
  }, []);

  // Use useEffect for navigation side effects
  useEffect(() => {
    if ((currentUser?.hasProduct > 0 || currentUser?.has_skipped_setup > 0) && currentAccountDetails?.account_number) {
      navigate("/store");
    }
  }, [currentUser, currentAccountDetails, navigate]);

  // New businesses (no product, no market setup) go through market selection
  // before the store. This is the Google signup / switch-account onboarding.
  const hasMarketSetup = currentUser?.market_setup_done > 0 || currentUser?.has_skipped_setup > 0;
  useEffect(() => {
    if (
      currentAccountDetails?.account_number &&
      !currentAccountDetails?.is_pre_paymeo &&
      currentUser?.hasProduct === 0 &&
      !hasMarketSetup
    ) {
      navigate("/setup/select-market");
    }
  }, [currentUser, currentAccountDetails, hasMarketSetup, navigate]);

  // Users who already selected a market or skipped setup are granted the
  // dashboard — never trap them in the complete-profile flow again.
  useEffect(() => {
    if (hasMarketSetup && currentUser?.hasProduct === 0) {
      navigate('/dashboard');
    }
  }, [hasMarketSetup, currentUser, navigate]);

  // The complete-profile flow (confirm business name → connect bank account)
  // finishes here: refresh the page data so the complete-profile card is
  // replaced by the actual page content (market selection / store redirects
  // fire automatically when applicable, otherwise the welcome content shows).
  const handleCompleteProfile = async () => {
    setHasBankAccount(true);
    setCheckingBankAccount(false);
    try {
      await refreshUserData();
      await refreshDashboardData();
    } catch (error) {
      console.error('Error refreshing after profile completion:', error);
    }
  };

  // Creating New accounts 
  const handleNewWalletAccount = async (formData) => {
    try {

      const submitData = new FormData();
      submitData.append("countryCode", formData.countryCode);
      submitData.append('phoneNumber', formData.phoneNumber);
      submitData.append('companyName', formData.companyName);
      submitData.append('userId', formData.userId);
      submitData.append('ownsBusiness', formData.ownsBusiness.toString());
      
      if (formData.ownsBusiness && formData.rc_number) {
        submitData.append('rc_number', formData.rc_number);
      }
      
      if (formData.ownsBusiness && formData.business_certificate) {
        submitData.append('business_certificate', formData.business_certificate);
      }

      // Payment account / virtual account creation is temporarily disabled.
      // const response = await api.post('/app/api/generate-existing-account', submitData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });
      const response = { data: { success: false, message: 'Account generation is temporarily disabled' } };

      if (response.data.success) {
        // Handle different success scenarios based on response
        if (response.data.requires_verification) {
          // Business verification submitted case
          showToast(
            'Your business documents have been submitted successfully and are under review. Your account will be verified within 48 hours. You can update other information, but your account will remain unverified until manual verification is completed.',
            'success',
            8000 // Longer duration for important message
          );
          
          // Don't refresh user data or reload page for verification submissions
          // The account is not created yet, so we keep the modal open for user to see the message
          return { requiresVerification: true };
        } else {
          // Normal account creation case
          showToast('Account generated successfully!', 'success');
          await refreshUserData();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return { requiresVerification: false };
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate account');
      }
    } catch (error) {
      console.error('Account generation error:', error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to generate account';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      throw error;
    }
  };

  const handleAddProduct = () => {
    // Only this CTA takes the user to the upload-product page.
    navigate('/setup/upload-product');
  };

  const updateSkipped = async () =>{
    await api.post(API_CONFIG.USER.UPDATE_SKIPPED, {
        user: currentUser.company_id
    })
  };

  const handleSkip = async () => {
    await updateSkipped();
    // Refresh so the Dashboard redirect logic sees the updated skip flag
    // instead of bouncing the user back to the complete-profile page.
    await refreshUserData();
    // Skip sends the user to the dashboard; uploading a product is only
    // reached via the "List My First Item" button.
    navigate('/dashboard');
  };

  // Show loading state while checking redirect
  if (currentUser?.hasProduct > 0 && currentAccountDetails?.account_number) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to store...</p>
        </div>
      </div>
    );
  }

  // Wait for the bank-account check before choosing what to render, so the
  // complete-profile card doesn't flash for users who already have one.
  if (checkingBankAccount) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 lg:py-8 sm:px-6 lg:px-8">
      <div className="max-w-[100vw] lg:max-w-4xl sm:w-full mx-auto">
        {currentUser?.name_change_status === "under-review" ? (
          // Account is under review
          <div className="mb-6">
            <AccountUnderReviewCard user={currentUser} />
          </div>
        ) : !currentAccountDetails?.account_number && !hasBankAccount ? (
          // No account, not under review, and no bank account yet — two-step
          // complete profile: confirm business name, then connect a bank account.
          <GenerateAccountCard 
            user={currentUser} 
            onCompleteProfile={handleCompleteProfile} 
          />
        ) : currentAccountDetails?.is_pre_paymeo > 0 ? (
          // Has account but needs to upgrade (pre_paymeo > 0)
          <GetNewAccountCard 
            OnCreateNewAccount={handleNewWalletAccount} 
            user={currentUser} 
          />
        ) : !currentUser?.hasProduct ? (
          // Welcome page - only shown to users with no products
          <div className="bg-white lg:rounded-2xl overflow-hidden shadow-sm">
            {/* Compact Hero */}
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-10 sm:px-10 sm:py-14">
              <div className="relative z-10">
                {/* Success Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                
                {/* Main Message */}
                <div className="text-center text-white">
                  <h1 className="text-2xl sm:text-3xl font-bold mb-2">
                      🎉 Profile Complete!
                  </h1>
                  <p className="text-base text-blue-100/90 max-w-lg mx-auto">
                    Start listing items and unlock the full potential of your Paymeo experience.
                  </p>
                </div>
              </div>
              
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }}></div>
              </div>
            </div>

            {/* CTA Section - immediately visible, above the fold */}
            <div className="px-6 pt-6 pb-4 sm:px-10 sm:pt-8 sm:pb-4">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button 
                  onClick={handleAddProduct}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3.5 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>List My First Item</span>
                </button>
                
                <button 
                  onClick={handleSkip}
                  className="w-full sm:w-auto border-2 border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 font-semibold py-3 px-8 rounded-xl transition-all duration-200 hover:bg-gray-50"
                >
                  Skip
                </button>
              </div>
              
              {/* Additional Info */}
              <p className="text-center text-xs text-gray-400 mt-3">
                You can always list items later from your dashboard
              </p>
            </div>

            {/* What's Next Section */}
            <div className="px-6 py-6 sm:px-10 sm:py-8 border-t border-gray-100">
              <h2 className="text-base font-semibold text-gray-900 mb-4 text-center">
                What's Next?
              </h2>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/80 border border-gray-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-sm text-gray-900">List Your First Item</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Showcase your products to thousands of potential buyers</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/80 border border-gray-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-sm text-gray-900">Grow Your Business</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Reach more customers and increase your sales</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/80 border border-gray-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-sm text-gray-900">Build Your Brand</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Establish your presence on Paymeo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CompleteSignupPage;